import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AssetTracking = () => {
  const { API } = useAuth();
  const [fleetStatus, setFleetStatus] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const saveTimers = useRef({});

  useEffect(() => {
    fetchFleetStatus();
  }, [statusFilter]);

  useEffect(() => {
    axios.get(`${API}/employees/names`)
      .then((res) => setEmployees(Array.isArray(res.data) ? res.data : []))
      .catch(() => setEmployees([]));
  }, []);

  const fetchFleetStatus = async () => {
    try {
      const params = statusFilter ? { status_filter: statusFilter } : {};
      const response = await axios.get(`${API}/fleet/live-status`, { params });
      setFleetStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch fleet status');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchFleetStatus();
  };

  const patchVehicle = async (id, payload) => {
    try {
      await axios.patch(`${API}/vehicles/${id}`, payload);
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleRemarksChange = (id, remarks) => {
    setFleetStatus((prev) => prev.map((item) => (item.id === id ? { ...item, tracking_remarks: remarks } : item)));
    if (saveTimers.current[id]) clearTimeout(saveTimers.current[id]);
    saveTimers.current[id] = setTimeout(() => patchVehicle(id, { tracking_remarks: remarks }), 1000);
  };

  const handleRmChange = (id, rmName) => {
    setFleetStatus((prev) => prev.map((item) => (item.id === id ? { ...item, rm_name: rmName } : item)));
    patchVehicle(id, { rm_name: rmName });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Use':
        return 'bg-blue-100 text-blue-700';
      case 'Parked':
        return 'bg-green-100 text-green-700';
      case 'Under Repair':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="asset-tracking-section">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Live Fleet Status</CardTitle>
              <CardDescription>Real-time vehicle tracking and status monitoring</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              data-testid="refresh-fleet-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Status Filter */}
          <div className="mb-6">
            <Label htmlFor="status-filter" className="mb-2 block">Filter by Status</Label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setLoading(true);
              }}
              className="flex h-10 w-64 rounded-md border border-input bg-background px-3 py-2 text-sm input-focus"
              data-testid="status-filter-select"
            >
              <option value="">All Vehicles</option>
              <option value="In Use">In Use</option>
              <option value="Under Repair">Under Repair</option>
              <option value="Parked">Parked</option>
            </select>
          </div>

          {/* Fleet Status Table */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="fleet-status-table">
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Sr No</TableHead>
                    <TableHead>Car No</TableHead>
                    <TableHead>Driver Id</TableHead>
                    <TableHead>Driver Name</TableHead>
                    <TableHead>Mobile Number</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>RM</TableHead>
                    <TableHead>Chassis No</TableHead>
                    <TableHead>Engine No</TableHead>
                    <TableHead>Remark</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fleetStatus.map((item, index) => (
                    <TableRow key={item.id} className="table-row">
                      <TableCell className="text-sm text-gray-500">{index + 1}</TableCell>
                      <TableCell className="font-mono font-semibold">
                        {item.vehicle_number}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {item.driver_id || <span className="text-gray-300">—</span>}
                      </TableCell>
                      <TableCell className="font-medium">
                        {item.driver_name || <span className="text-gray-300">—</span>}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {item.mobile_number || <span className="text-gray-300">—</span>}
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <select
                          value={item.rm_name || ''}
                          onChange={(e) => handleRmChange(item.id, e.target.value)}
                          className="h-9 min-w-[130px] rounded-md border border-input bg-background px-2 text-sm input-focus"
                          data-testid={`rm-select-${item.id}`}
                        >
                          <option value="">— Select RM —</option>
                          {employees.map((emp) => (
                            <option key={emp.id} value={emp.name}>{emp.name}</option>
                          ))}
                          {item.rm_name && !employees.some((emp) => emp.name === item.rm_name) && (
                            <option value={item.rm_name}>{item.rm_name}</option>
                          )}
                        </select>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {item.chassis_number || <span className="text-gray-300">—</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {item.engine_number || <span className="text-gray-300">—</span>}
                      </TableCell>
                      <TableCell>
                        <textarea
                          value={item.tracking_remarks || ''}
                          onChange={(e) => handleRemarksChange(item.id, e.target.value)}
                          placeholder="Add remark..."
                          className="w-full min-w-[180px] rounded-md border border-input bg-background px-2 py-1 text-sm input-focus resize-none"
                          rows="2"
                          data-testid={`remarks-${item.id}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {fleetStatus.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                        No vehicles found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Summary Stats */}
          {!loading && fleetStatus.length > 0 && (
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 font-medium">In Use</p>
                <p className="text-2xl font-bold text-blue-700">
                  {fleetStatus.filter(v => v.status === 'In Use').length}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600 font-medium">Parked</p>
                <p className="text-2xl font-bold text-green-700">
                  {fleetStatus.filter(v => v.status === 'Parked').length}
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm text-red-600 font-medium">Under Repair</p>
                <p className="text-2xl font-bold text-red-700">
                  {fleetStatus.filter(v => v.status === 'Under Repair').length}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AssetTracking;
